import { formatNumber } from '@angular/common';
import { AgGridConfig } from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import { ColDef, ColumnApi, ColumnState } from 'ag-grid-community';
import dayjs from 'dayjs';

export function applyColumnState(
  columnApi: ColumnApi,
  columnState: ColumnState[]
) {
  try {
    columnApi.applyColumnState({
      state: columnState,
      applyOrder: true
    });
  } catch (e) {
    console.error('Unable to load column state');
  }
}

export function mapHeaders(setting: AgGridConfig, globalDef: AgGridConfig) {
  const defaultHeaders = globalDef.headers as Header[];
  const settingHeaders = setting.headers as string[];
  return settingHeaders.map(headerName => {
    return defaultHeaders.find(header => header.field === headerName);
  });
}

export function setColumnsColors(headers: any[]): void {
  headers.forEach((header: ColDef) => {
    header.cellStyle = params => {
      if (params.node?.footer) {
        return {
          color: 'var(--label-grey)'
        };
      } else {
        if (typeof params.value !== 'number') {
          return {
            color: 'white'
          };
        } else {
          return {
            color:
              params.value < 0 ? 'var(--negative-red)' : 'var(--positive-green)'
          };
        }
      }
    };
  });
}

export function chartOptions(
  titleText: string,
  rowDataWithCumulativePnl: {
    timestamp: Date;
    cumulativePNL: number;
    pnlInReportingCurrency: number;
    isBackToBack: boolean;
  }[]
): any {
  return {
    background: {
      fill: '#202424'
    },
    padding: {
      right: 60,
      top: 40
    },
    title: {
      text: titleText,
      color: 'white'
    },
    series: [
      {
        type: 'scatter',
        xKey: 'timestamp',
        yKey: 'pnlInReportingCurrency',
        yName: ' Trade PNL (LHS) ',
        data: rowDataWithCumulativePnl,
        fill: '#44c8b5',
        stroke: '#44c8b5',
        size: 12,
        tooltip: {
          renderer: params => {
            const date = dayjs
              .utc(params.datum.timestamp)
              .format('D MMM YYYY hh:mm');
            const roundedPnl = formatNumber(
              params.datum.pnlInReportingCurrency,
              'en-GB',
              '1.0-0'
            );
            return {
              content: `${date}: ${roundedPnl}`,
              backgroundColor: '#333333',
              color: 'white',
              title: null
            };
          }
        }
      },
      {
        type: 'line',
        xKey: 'timestamp',
        yKey: 'cumulativePNL',
        yName: ' Cumulative PNL (RHS) ',
        data: rowDataWithCumulativePnl,
        stroke: '#0c5174',
        strokeWidth: 5,
        marker: {
          enabled: false
        },
        highlightStyle: {
          series: {
            strokeWidth: 5
          }
        },
        tooltip: {
          renderer: params => {
            const date = dayjs
              .utc(params.datum.timestamp)
              .format('D MMM YYYY hh:mm');
            const roundedCumulativePnl = formatNumber(
              params.datum.cumulativePNL,
              'en-GB',
              '1.0-0'
            );
            return {
              content: `${date}: ${roundedCumulativePnl}`,
              backgroundColor: '#333333',
              color: 'white',
              title: null
            };
          }
        }
      }
    ],

    axes: [
      {
        position: 'bottom',
        type: 'time',
        gridLine: {
          style: [
            {
              stroke: '#5e615f',
              lineDash: [5, 5]
            }
          ]
        },
        line: {
          enabled: true
        },
        title: { text: 'Time (UTC)', color: 'white' },
        label: {
          color: 'white',
          rotation: 45,
          fontSize: 12,
          formatter: params => {
            return dayjs.utc(params.value).format('D MMM YYYY');
          }
        }
      },
      {
        position: 'left',
        type: 'number',
        min: 0,
        gridLine: {
          style: [
            {
              stroke: '#5e615f',
              lineDash: [5, 5]
            }
          ]
        },
        line: {
          enabled: true
        },
        title: { text: 'Trade PNL', color: 'white' },
        label: {
          color: 'white',
          formatter: params => params.value.toLocaleString()
        },
        keys: ['pnlInReportingCurrency']
      },
      {
        position: 'top',
        type: 'number',
        line: {
          enabled: true
        },
        title: { text: '', color: '#ffffff', fontSize: 14 },
        label: {
          color: '#ffffff',
          fontSize: 12
        }
      },
      {
        position: 'right',
        type: 'number',
        gridLine: {
          style: [
            {
              stroke: '#5e615f',
              lineDash: [5, 5]
            }
          ]
        },
        line: {
          enabled: true
        },
        title: { text: 'Cumulative PNL', color: 'white' },
        label: {
          color: 'white',
          formatter: params => params.value.toLocaleString()
        },
        keys: ['cumulativePNL']
      }
    ],
    legend: {
      item: {
        label: {
          color: 'white',
          fontSize: 12
        }
      }
    }
  };
}
